import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { fetchBorrowers } from "../../../api/borrowers";
import BorrowerSelect from "../Borrowers/BorrowerSelect";
import { formatBorrowerLabel } from "../Borrowers/borrowerSelectUtils";
import { sendCommunication } from "../../../api/communications";
import { CHANNELS, TEMPLATE_KEYS, isCollectionTemplate, templateMeta } from "./communicationsMeta";
import { runCrud } from "../Common/credoraToast";
import { CREDORA_MODAL_CLASS, CREDORA_MODAL_CONTAINER } from "../Common/credoraModalUtils";

const emptyVars = () => ({
  amount: "",
  due_date: "",
  reference: "",
  customer_name: "",
  holiday_name: "",
  years: "",
  survey_link: "",
  offer_details: "",
  product_name: "",
});

const SendMessageModal = ({
  isOpen,
  toggle,
  borrower,
  onSent,
  initialTemplateKey,
  initialVariables,
  initialIsCollection = false,
  caseNotes = "",
  channelHealth = [],
}) => {
  const [borrowers, setBorrowers] = useState([]);
  const [loadingBorrowers, setLoadingBorrowers] = useState(false);
  const [borrowerId, setBorrowerId] = useState("");
  const [templateKey, setTemplateKey] = useState("payment_received");
  const [channel, setChannel] = useState("sms");
  const [variables, setVariables] = useState(emptyVars());
  const [isCollection, setIsCollection] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const meta = useMemo(() => templateMeta(templateKey), [templateKey]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setError("");
    setVariables({ ...emptyVars(), ...(initialVariables || {}) });
    if (initialTemplateKey) {
      setTemplateKey(initialTemplateKey);
      setIsCollection(initialIsCollection || isCollectionTemplate(initialTemplateKey));
    } else {
      setTemplateKey("payment_received");
      setIsCollection(initialIsCollection);
    }
    if (borrower?.id) {
      setBorrowerId(borrower.id);
    } else {
      setBorrowerId("");
      setLoadingBorrowers(true);
      fetchBorrowers(0, 500)
        .then(setBorrowers)
        .catch(() => setBorrowers([]))
        .finally(() => setLoadingBorrowers(false));
    }
  }, [isOpen, borrower, initialTemplateKey, initialVariables, initialIsCollection]);

  const channelAvailable = useMemo(() => {
    const map = {};
    CHANNELS.forEach((c) => {
      map[c.value] = true;
    });
    channelHealth.forEach((ch) => {
      map[ch.channel] = Boolean(ch.primary_gateway);
    });
    return map;
  }, [channelHealth]);

  useEffect(() => {
    setIsCollection(isCollectionTemplate(templateKey));
  }, [templateKey]);

  const handleTemplateChange = (key) => {
    setTemplateKey(key);
    setIsCollection(isCollectionTemplate(key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!borrowerId) {
      setError("Select a borrower.");
      return;
    }
    setSending(true);
    setError("");
    const varPayload = {};
    (meta?.variables || []).forEach((name) => {
      const val = variables[name];
      if (val) {
        varPayload[name] = String(val);
      }
    });
    try {
      await runCrud(
        () =>
          sendCommunication({
            borrower_id: borrowerId,
            template_key: templateKey,
            channel,
            variables: varPayload,
            is_collection_message: isCollection,
          }),
        { successMessage: "Message sent successfully." }
      );
      onSent?.();
      toggle();
    } catch {
      /* error toast shown by runCrud */
    } finally {
      setSending(false);
    }
  };

  const borrowerLabel = (b) => formatBorrowerLabel(b);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      className={CREDORA_MODAL_CLASS}
      container={CREDORA_MODAL_CONTAINER}>
      <ModalHeader toggle={toggle}>Send message</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {error && <Alert color="danger">{error}</Alert>}
          {caseNotes && (
            <Alert color="light" className="border small mb-3">
              {caseNotes}
            </Alert>
          )}
          {!borrower && (
            <FormGroup>
              <Label for="send-message-borrower">Borrower</Label>
              {loadingBorrowers ? (
                <div className="d-flex align-items-center gap-2 text-muted py-2">
                  <Spinner size="sm" />
                  Loading borrowers…
                </div>
              ) : (
                <BorrowerSelect
                  inputId="send-message-borrower"
                  borrowers={borrowers}
                  value={borrowerId}
                  onChange={setBorrowerId}
                  isDisabled={sending}
                  placeholder="Search borrower by name or phone…"
                />
              )}
            </FormGroup>
          )}
          {borrower && (
            <p className="text-muted small mb-3">
              To: <strong>{borrowerLabel(borrower)}</strong>
            </p>
          )}
          <FormGroup>
            <Label>Template</Label>
            <Input type="select" value={templateKey} onChange={(e) => handleTemplateChange(e.target.value)}>
              {TEMPLATE_KEYS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Channel</Label>
            <Input type="select" value={channel} onChange={(e) => setChannel(e.target.value)}>
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value} disabled={channelHealth.length > 0 && !channelAvailable[c.value]}>
                  {c.label}
                  {channelHealth.length > 0 && !channelAvailable[c.value] ? " (no gateway)" : ""}
                </option>
              ))}
            </Input>
          </FormGroup>
          {(meta?.variables || []).map((name) => (
            <FormGroup key={name}>
              <Label className="text-capitalize">{name.replace(/_/g, " ")}</Label>
              <Input
                value={variables[name] || ""}
                onChange={(e) => setVariables((v) => ({ ...v, [name]: e.target.value }))}
                placeholder={name === "due_date" ? "YYYY-MM-DD" : name === "amount" ? "500000" : ""}
                required={name === "amount"}
              />
            </FormGroup>
          ))}
          {meta?.collection && (
            <FormGroup check className="mb-0">
              <Label check>
                <Input
                  type="checkbox"
                  checked={isCollection}
                  onChange={(e) => setIsCollection(e.target.checked)}
                />{" "}
                Collection message (registered borrower contact only — COL-001)
              </Label>
            </FormGroup>
          )}
          <p className="text-muted small mt-3 mb-0">
            Language follows borrower preference. Institution prefix is added automatically (PLT-003).
          </p>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={toggle}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default SendMessageModal;
